import { type CVEditingSection, type FullCV } from '@cibli/shared';
import { useMemo } from 'react';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import type { EditableSectionSplit } from '../../../hooks/usePageDelimitation';

import EditableSection from '../../../components/form/EditableSection';
import { isNotEmpty } from '../../../utils/is-not-empty';

interface EducationProps {
  education: FullCV['education'];
  accent: string;
  onEdit?: (section: CVEditingSection) => void;
  chunk?: EditableSectionSplit;
}

export default function Education({ education, accent, onEdit, chunk }: EducationProps) {
  const { t, formatDate } = useCVTranslation();
  const handleEdit = () => onEdit && onEdit('education');
  const editable = useMemo(() => onEdit !== undefined, [onEdit]);

  if (!education?.length && !editable) return null;

  const start = chunk?.startIndex ?? 0;
  const end = chunk?.endIndex ?? education?.length ?? 0;
  const showTitle = chunk?.showTitle ?? true;
  const items = education?.slice(start, end) ?? [];

  return (
    <EditableSection onEdit={handleEdit} editable={editable} accentColor={accent} splitable={true}>
      <div className="rounded-lg border p-2.5">
        {showTitle && (
          <div data-splitable-header className="flex items-center gap-1 mb-4">
            <div className="h-[10px] w-[10px] rounded-sm" style={{ backgroundColor: accent }} />
            <h2 className="text-[11px] font-black uppercase tracking-widest text-black">
              {t('CV.education')}
            </h2>
          </div>
        )}
        {!showTitle && <div className="h-3" aria-hidden />}
        <div className="space-y-5">
          {items.map((edu, i) => (
            <div
              key={start + i}
              data-splitable-item
              className="break-inside-avoid [page-break-inside:avoid]"
            >
              <div className="flex items-start justify-between">
                <div>
                  {isNotEmpty(edu.school) && (
                    <h3 className="font-bold text-[14px]">{edu.school}</h3>
                  )}
                  {(isNotEmpty(edu.degree) || isNotEmpty(edu.name)) && (
                    <p className="text-[14px] font-semibold">
                      {isNotEmpty(edu.name) ? `${edu.degree} – ${edu.name}` : edu.degree}
                    </p>
                  )}
                </div>
                <div className="text-right text-black text-[12.75px]">
                  <p>
                    {isNotEmpty(edu.degreeStartedAt) &&
                    edu.degreeStartedAt !== 'CURRENT' &&
                    isNotEmpty(edu.degreeObtainedAt) &&
                    edu.degreeObtainedAt !== 'CURRENT'
                      ? `${formatDate(edu.degreeStartedAt!, { month: 'short', year: 'numeric' })} – ${formatDate(edu.degreeObtainedAt!, { month: 'short', year: 'numeric' })}`
                      : isNotEmpty(edu.degreeObtainedAt) && edu.degreeObtainedAt !== 'CURRENT'
                        ? formatDate(edu.degreeObtainedAt!, { month: 'short', year: 'numeric' })
                        : isNotEmpty(edu.degreeStartedAt) && edu.degreeStartedAt !== 'CURRENT'
                          ? formatDate(edu.degreeStartedAt!, { month: 'short', year: 'numeric' })
                          : ''}
                  </p>
                </div>
              </div>
              {edu.acquiredSkills?.filter(s => isNotEmpty(s))?.length ? (
                <p className="text-[13px] text-black mt-1 font-semibold">
                  {edu.acquiredSkills.filter(s => isNotEmpty(s)).join(' · ')}
                </p>
              ) : null}
              {i !== items.length - 1 && (
                <div data-splitable-separator className="h-px bg-gray-200 mt-4" />
              )}
            </div>
          ))}
        </div>
      </div>
    </EditableSection>
  );
}
